/**
 * Member.tsx
 * @description :: This page contains all the Members information for admin purposes.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  ScrollArea,
  TextInput,
  rem,
  Text,
  UnstyledButton,
  Center,
  Select,
  Modal,
  Pagination,
  Checkbox,
  Badge,
  Button,
} from "@mantine/core";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconEye,
  IconTrash,
  IconChecks,
  IconEdit,
  IconArrowBadgeUp,
  // IconFileExport,
  IconTableExport,
  IconX,
  IconCheck,
  IconRestore,
} from "@tabler/icons-react";
import * as XLSX from 'xlsx';
import { RowDataMember } from "../MemberPage/types";
import { props } from "./props";
import classes from "./Member.module.css";
import AddCustomer from "../MemberPage/AddCustomer";
import EditCustomer from "./EditCustomer";
import DeleteCustomer from "./DeleteCustomer";
import RestoreCustomer from "./RestoreCustomer";
import EnableFastPass from "./EnableFastPass";
import useAxiosPrivate from "../../auth/useAxiosPrivate";
import { useFetchRowData } from "../hooks/useFetchRowData";
import { member } from "../types/RowDataTypes";
import { CustomTable } from "../Table/CustomTable";
import { SearchBar } from "../SearchBar/SearchBar";
import {
  showErrorAlert,
  showSuccessAlert,
} from "../../helper/alerts";
import useAuth from "../../auth/useAuth";
import Header from "../static/Header";
import getMemberTable from "../../DataDisplay/DataDisplayTable/MemberTable";
import ScoreCard from "./ScoreCard";

import { formatDD_MM_YYYY } from "../../helper/DateTime";

// Searching and Sorting
enum ColumnHeaderToFilterPair {
  NAME = "Name",
  CONTACT_NO = "Contact No",
  TEMP_CODE = "Code",
  EMAIL = "Email",
  IC_NO = "IC No",
  IC_FULLNAME = "IC Fullname",
//   PROFILE_PICTURE = "Profile Picture",
//   EXPO_TOKEN = "Expo Push Token",
// DOB = "DOB",
//   REFERRAL = "Referral",
//   TEMP_CODE_TIME = "Temp Code Time",
//   MEMBERSHIP_TYPE = "Membership Type",
  TYPE = "Type",
//   COMPANY = "Company",
  // COINS = "Coins",
  // CASH = "Cash",
  MEMBERSHIP_TYPE = "Membership",
  CREATED_AT = "Registered At",
  DELETED = "Deleted"
//   DAILY = "Daily",
}

const colFilter = ColumnHeaderToFilterPair;

const colFilters: { [key in ColumnHeaderToFilterPair]: string } = {
  [colFilter.NAME]: "fullname",
  [colFilter.EMAIL]: "email",
  [colFilter.CONTACT_NO]: "phone_number",
  [colFilter.TEMP_CODE]: "tempCode",
  [colFilter.IC_NO]: "nric",
  [colFilter.IC_FULLNAME]: "nric_fullname",
//   [colFilter.PROFILE_PICTURE]: "",
//   [colFilter.EXPO_TOKEN]: "",
// [colFilter.DOB]: "dob",
//   [colFilter.REFERRAL]: "referral",
//   [colFilter.TEMP_CODE_TIME]: "",
//   [colFilter.MEMBERSHIP_TYPE]: "membershipType",
  [colFilter.TYPE]: "",
//   [colFilter.COMPANY]: "company",
  // [colFilter.COINS]: "coins",
  // [colFilter.CASH]: "cash",
  [colFilter.MEMBERSHIP_TYPE]: "membershipType",
  [colFilter.CREATED_AT]: "createdAt",
  [colFilter.DELETED]: "deleted",
//   [colFilter.DAILY]: "",
};

function Th({ children, sortable, reversed, sorted, onSort }: props) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  const allowedSorts = [
    colFilter.NAME.toLowerCase(),
    colFilter.EMAIL.toLowerCase(),
    // colFilter.CASH.toLowerCase(),
    // colFilter.COINS.toLowerCase(),
    colFilter.CONTACT_NO.toLowerCase(),
    // colFilter.MEMBERSHIP_TYPE.toLowerCase(),
    // colFilter.COMPANY.toLowerCase(),
  ];

  return (
    <Table.Th className={`${classes.th} ${sortable ? classes.sortable : ""}`}>
      {allowedSorts.includes(children!!.toString().toLowerCase()) ? (
        <UnstyledButton onClick={onSort} className={classes.control}>
          <div className={classes.sortableHeader}>
            <Text fw={500} fz="sm">
              {children}
            </Text>
            <Center className={classes.icon}>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </div>
        </UnstyledButton>
      ) : (
        <Text fw={500} fz="sm">
          {children}
        </Text>
      )}
    </Table.Th>
  );
}

export const Member = () => {
  const [search, setSearch] = useState<string>("");

  //Used in searching and when deleting to cause rerender to fetch data
  const [tableStateChanged, setTableStateChanged] = useState(0);

  const changeTableState = () => {
    setTableStateChanged(Math.random() * Math.random());
  };

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  function resetPage() {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return true;
    }
    return false;
  }

  const checkboxesFilter: JSX.Element[] = [];

  //Set allowed search filter checkbox to be shown
  const allowedFilters = [
    colFilters[colFilter.NAME],
    colFilters[colFilter.EMAIL],
    colFilters[colFilter.CONTACT_NO],
    colFilters[colFilter.IC_NO],
    colFilters[colFilter.MEMBERSHIP_TYPE],
    colFilters[colFilter.DELETED],
    // colFilters[colFilter.COMPANY],
  ];
  for (const [key, value] of Object.entries(colFilters)) {
    if (allowedFilters.includes(value)) {
      const checkboxFilter: JSX.Element = (
        <Checkbox value={value} label={key} />
      );
      checkboxesFilter.push(checkboxFilter);
    }
  }

  const [showFilterOptions, setShowFilterOptions] = useState(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setSearch(input);
  };

  //Sorting?
  const [sortBy, setSortBy] = useState<keyof RowDataMember | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  //For pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const itemsOnPage = useRef(0);

  //Assigining fetched data
  const fetchedData = useFetchRowData(
    itemsPerPage,
    currentPage,
    search,
    selectedFilters,
    tableStateChanged,
    sortBy ?? "createdAt",
    reverseSortDirection ? "DESC" : "ASC",
    member,
    getMemberTable().create()
  );

  const totalRows: number = fetchedData.totalRows;
  const rowData: RowDataMember[] | null = fetchedData.data as RowDataMember[];
  const isLoading: boolean = fetchedData.isLoading;
  const [rowDataMembers, setRowDataMembers] = useState<RowDataMember[] | null>(
    null
  );

  const axiosPrivate = useAxiosPrivate();

  // Separate state for the modal related to adding customers
  const [isAddCustomerFormVisible, setAddCustomerFormVisible] = useState(false);

  const { auth } = useAuth();
  // Separate state for the modal related to viewing/editing customers
  const [selectedRow, setSelectedRow] = useState<RowDataMember | null>(null);
  const [isViewMode, setViewMode] = useState(false);
  const [isEditMode, setEditMode] = useState(false);
  const [isFastPassModalVisible, setFastPassModalVisible] = useState(false);


  const [isDeleteConfirmationVisible, setDeleteConfirmationVisible] =
    useState(false);
  const [rowToDelete, setRowToDelete] = useState<RowDataMember | null>(null);

  const [isRestoreConfirmationVisible, setRestoreConfirmationVisible] =
    useState(false);
  const [rowToRestore, setRowToRestore] = useState<RowDataMember | null>(null);

  const handleView = (row: RowDataMember) => {
    setSelectedRow(row);
    setViewMode(true);
  };

  const handleEdit = (row: RowDataMember) => {
    setSelectedRow(row);
    setEditMode(true);
  };

  const handleFastPass = (row: RowDataMember) => {
    setSelectedRow(row);
    setFastPassModalVisible(true);
  };

  const handleDelete = (row: RowDataMember) => {
    setRowToDelete(row);
    setDeleteConfirmationVisible(true);
  };

  const handleRestore = (row: RowDataMember) => {
    setRowToRestore(row);
    setRestoreConfirmationVisible(true);
  };

  const handleConfirmRestore = () => {
    if (rowToRestore) {
      axiosPrivate
        .post("/api/accounts/restore", {
          uid: rowToRestore.uid,
          phone_number: rowToRestore.contactNo,
        })
        .then((response) => {
          setRestoreConfirmationVisible(false);
          changeTableState();
        })
        .catch((err) => {
          showErrorAlert("An error has occured");
        });
    }
  };

  const handleConfirmDelete = () => {
    if (rowToDelete) {
      axiosPrivate
        .post("/api/accounts/delete", {
          uid: rowToDelete.uid,
          phone_number: rowToDelete.contactNo,
        })
        .then((response) => {
          setDeleteConfirmationVisible(false);
          if (
            itemsOnPage.current == 1 &&
            currentPage !== 1 &&
            currentPage == Math.ceil(totalRows / itemsPerPage)
          )
            setCurrentPage((prev) => prev - 1);
          else changeTableState();
        })
        .catch((err) => {
          showErrorAlert("An error has occured");
        });
    }
  };

  const handleConfirmEnableFastPass = () => {
    if (selectedRow) {
      axiosPrivate
        .post("/api/admin/membership/toggle-fast-pass", {
          uid: selectedRow.uid,
          name: auth.name
        })
        .then((response) => {
          setFastPassModalVisible(false);
          changeTableState();
        })
        .catch((err) => {
          showErrorAlert("An error has occured");
        });
    }
  };

  const handleCancelDelete = () => {
    setRowToDelete(null);
    setDeleteConfirmationVisible(false);
  };

  const handleCancelRestore = () => {
    setRowToRestore(null);
    setRestoreConfirmationVisible(false);
  };

  const setSorting = (field: keyof RowDataMember) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    changeTableState();
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const handlePageChange = (newPage: number) => {
    // console.log(newPage);
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setCurrentPage(1);
    setItemsPerPage(value);
  };

  // const handleAddCustomers = () => {
  //   setAddCustomerFormVisible(true);
  // };

  const handleCloseModal = () => {
    setAddCustomerFormVisible(false);
  };

  const handleAddCustomer = (
    newCustomer: RowDataMember,
    onSuccess: () => void,
    onFailure: () => void
  ) => {
    axiosPrivate
      .post("/api/accounts/register", {
        fullname: newCustomer.name,
        phone_number: newCustomer.contactNo,
        email: `ivan@IconTestPipe${Math.random() * 10}.com`, //Replace email with form data once field is added to form
      })
      .then((response) => {
        if (itemsOnPage.current < itemsPerPage) {
          setRowDataMembers((prevData) => [...prevData!!, newCustomer]);
        }
        itemsOnPage.current = itemsOnPage.current + 1;

        onSuccess();
      })
      .catch((error) => {
        // console.log("error " + error);
      });
  };

  useEffect(() => {
    setRowDataMembers(rowData);
    setMemberScoreCard();
    itemsOnPage.current = fetchedData.itemsOnPage;
  }, [rowData]);

  const handleSaveChanges = (editedCustomer: RowDataMember) => {
    // Validate the edited data, similar to handleAddCustomer if needed

    // console.log(editedCustomer);
    axiosPrivate
      .post("/api/accounts/edit-profile", {
        fullname: editedCustomer.name,
        uid: editedCustomer.uid,
        email: editedCustomer.email,
        phone_number: editedCustomer.contactNo,
        nric: editedCustomer.nric,
        nric_fullname: editedCustomer.nric_fullname,
        dob: editedCustomer.dob,
      })
      .then((response) => {
        // Find the index of the selectedRow in the sortedData
        const indexOfSelectedRow = rowDataMembers!!.findIndex(
          (row) => row.uid === selectedRow?.uid
        );

        // Create a copy of the sortedData
        const updatedData = [...rowDataMembers!!];

        // Update the selectedRow in the copied data
        updatedData[indexOfSelectedRow] = {
          name: editedCustomer.name,
          email: editedCustomer.email,
          nric: editedCustomer.nric,
          nric_fullname: editedCustomer.nric_fullname,
          contactNo: editedCustomer.contactNo,
          profilePicture: editedCustomer.profilePicture,
          expoPushToken: editedCustomer.expoPushToken,
          referral: editedCustomer.referral,
          tempCode: editedCustomer.tempCode,
          tempCodeTime: editedCustomer.tempCodeTime,
          membershipType: editedCustomer.membershipType,
          isDealer: editedCustomer.isDealer,
          company: editedCustomer.company,
          coins: editedCustomer.coins,
          cash: editedCustomer.cash,
          daily: editedCustomer.daily,
          action: "Edit",
          uid: editedCustomer.uid,
          dob: editedCustomer.dob,
          isVerified: editedCustomer.isVerified,
          isFastPass: editedCustomer.isFastPass,
          createdAt: editedCustomer.createdAt,
          isCorporate: editedCustomer.isCorporate,
          deleted: editedCustomer.deleted,
        };

        // changeTableState();
        setRowDataMembers(updatedData);

        // Close the modal and exit edit mode
        setEditMode(false);
      })
      .catch((error) => {
        // console.log("error " + error);
      });
  };

  const rowsToMap = rowDataMembers ?? [];
  const rows = rowsToMap.map((row, index) => (
    <Table.Tr key={index}>
      <Table.Td>{startIndex + index + 1}</Table.Td>
      <Table.Td>{row.name}</Table.Td>
      <Table.Td>+60 {row.contactNo}</Table.Td>
      <Table.Td>{row.tempCode}</Table.Td>
      <Table.Td>{row.email}</Table.Td>
      <Table.Td>{row.nric}</Table.Td>
      
      {/* <Table.Td>{row.profilePicture}</Table.Td> */}
      {/* <Table.Td>{row.expoPushToken}</Table.Td> */}
      {/* <Table.Td>{formatDD_MM_YYYY(row.dob)}</Table.Td> */}
      <Table.Td>{row.referral}</Table.Td>
      {/* <Table.Td>{row.tempCodeTime}</Table.Td> */}
      {/* <Table.Td>{row.membershipType}</Table.Td> */}
      <Table.Td>
        {
        row.isDealer ? 
            row.isVerified ? 
            <Badge variant="outline" color="green" rightSection={<IconCheck style={{ width: rem(12), height: rem(12) }} />}>Dealer</Badge>
            :
            <Badge variant="outline" color="orange" rightSection={<IconX style={{ width: rem(12), height: rem(12) }} />}>Dealer</Badge>
        : 
        row.isCorporate ? 
            row.isVerified ?
            <Badge variant="outline" color="green" rightSection={<IconCheck style={{ width: rem(12), height: rem(12) }} />}>Corporate</Badge>
            :
            <Badge variant="outline" color="orange" rightSection={<IconX style={{ width: rem(12), height: rem(12) }} />}>Corporate</Badge>
        : row.nric ?
            <Badge variant="outline" color="green" rightSection={<IconCheck style={{ width: rem(12), height: rem(12) }} />}>Member</Badge>
            :
            <Badge variant="outline" color="orange" rightSection={<IconX style={{ width: rem(12), height: rem(12) }} />}>Member</Badge>
        }
      
      </Table.Td>
      {/* <Table.Td>{row.company}</Table.Td> */}
      {/* <Table.Td>{row.coins ? row.coins : 0}</Table.Td>
      <Table.Td>{row.cash ? row.cash : 0}</Table.Td> */}
      <Table.Td>{row.membershipType}</Table.Td>
      <Table.Td>{formatDD_MM_YYYY(row.createdAt)}</Table.Td>
      {/* <Table.Td>{row.daily}</Table.Td> */}
      <Table.Td className={classes.actionButtons}>
        <button onClick={() => handleView(row)} className={classes.viewbutton}>
          <IconEye /> View
        </button>
        <button onClick={() => handleEdit(row)} className={classes.editbutton}>
          <IconEdit /> Edit
        </button>
        {!row.isFastPass ?
        <button onClick={() => handleFastPass(row)} className={classes.fastPassbutton}>
            <IconArrowBadgeUp /> Enable Fast Pass
        </button>
        :
        <button className={classes.fastPassEnabledbutton}>
            <IconChecks /> Fast Pass Enabled
        </button>
        }
        {/* If row is deleted, show Restore Button, else show Delete Button */}
        {row.deleted ?
          <button onClick={() => handleRestore(row)} className={classes.restorebutton}>
            <IconRestore /> Restore
          </button>
          :
          <button onClick={() => handleDelete(row)} className={classes.trashbutton}>
            <IconTrash /> Delete
          </button>
        }
      </Table.Td>
    </Table.Tr>
  ));

  const handleSelectPage = (selectedPage: number) => {
    if (selectedPage != currentPage) setCurrentPage(selectedPage);
  };

  const [membersRegTdy, setMembersRegTdy] = useState(0);
  const [membersRegTotal, setMembersRegTotal] = useState(0);

  const currentDateMY = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Asia/Kuala_Lumpur" })
  );

  const setMemberScoreCard = () => {
    axiosPrivate
      .post("/api/admin/admin/get-memberpage-stats", {
        currentDate: currentDateMY.toISOString().slice(0, 10),
      })
      .then((response) => {
        // console.log(response.data)
        setMembersRegTdy(response.data.memberCountToday);
        setMembersRegTotal(response.data.memberCountTotal);
        console.log(membersRegTdy)
        console.log(membersRegTotal)
      })
      .catch((err) => {
        showErrorAlert(err)
      });
  }

  const handleExportTable = async () => {
    try {
      const response = await axiosPrivate.get('/api/admin/admin/export-all');

      const data = response.data.data;

      // Format the data for Excel
      const formattedData = data.map((row: any, index: number) => ({
        'No.': index + 1,
        'Member Name': row.fullname,
        'Phone Number': row.phone_number,
        'Email': row.email,
        'IC No.': row.nric,
        'IC Full Name': row.nric_fullname,
        'Membership Status': row.isVerified,
        'Membership Type': row.membershipType,
        'Registered Date': new Intl.DateTimeFormat("en-MY", {
          timeZone: "Asia/Kuala_Lumpur",
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true
        }).format(new Date(row.createdAt)),
      }));

      // Create worksheet and workbook
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'All Orders');

      // Set column widths
      worksheet['!cols'] = [
        { wch: 5 },  // No.
        { wch: 40 }, // Member Name
        { wch: 15 }, // Phone Number
        { wch: 30 }, // Email
        { wch: 15 }, // IC No.
        { wch: 25 }, // IC Full Name
        { wch: 20 }, // Membership Status
        { wch: 20 }, // Membership Type
        { wch: 30 }, // Registered Date
      ];

      // Generate filename with timestamp
      const timestamp = new Date().toLocaleDateString('en-CA', { 
        timeZone: 'Asia/Kuala_Lumpur' 
      });
      const filename = `ViewnetMembers_Export_${timestamp}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);
      
      showSuccessAlert('Export successful!');
    } catch (error) {
      console.error('Export error:', error);
      showErrorAlert('Failed to export data');
    }
  };

  return (
    <div className={classes.memberContainer}>
      <ScrollArea className={classes.parentContainer}>
        <Header title="Member" />
        <div className={classes.searchBarContainer}>
          <div style={{ flex: 1 }}>
            <SearchBar
              isLoading={isLoading}
              resetPage={resetPage}
              changeTableState={changeTableState}
              search={search}
              handleSearch={handleSearch}
              setShowFilterOptions={setShowFilterOptions}
              showFilterOptions={showFilterOptions}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              checkboxesFilter={checkboxesFilter}
            />
          </div>
          <div className={classes.scoreCardRow}>
            <ScoreCard 
              score={isLoading? "-" : membersRegTdy} 
              title={`Members Registered (Today, ${currentDateMY.toLocaleDateString("en-GB")})`}/>
            <ScoreCard 
              score={isLoading? "-" : membersRegTotal} 
                title={`Total Registered Members`}/>
            <div className={classes.card} >
              <Button
                onClick={handleExportTable}
                className={classes.exportButton}
                >
                <div>
                  <IconTableExport size={30} stroke={1.5} />
                  <Text fw={700} fz="md" className={classes.cardTitle} >
                    Export Table
                  </Text> 
                </div>
              </Button>
            </div>
          </div>
          <div className={classes.exportButtonsContainer}></div>
        </div>
        <ScrollArea
          w={
            window.innerWidth -
            document.getElementById("nav-bar")!!.getBoundingClientRect().width
          }
        >
          <CustomTable
            // ColumnHeaderToFilterPair to exclude "deleted" column within the table
            colHeaders={
              Object.values(ColumnHeaderToFilterPair).filter(
                (ColumnHeader) => ColumnHeader !== colFilter.DELETED
              )
            }
            filters={Object.values(colFilters)}
            reverseSortDirection={reverseSortDirection}
            sortBy={sortBy}
            setSorting={setSorting}
            customclasses={classes}
            Th={Th}
            rowData={rowDataMembers}
            rows={rows}
          />
        </ScrollArea>
        <div className={classes.footerContainer}>
          <div className={classes.resultCount}>
            <Text>
              {`Showing `}
              <span style={{ fontWeight: "bold" }}>{startIndex + 1}</span>
              {`-`}
              <span style={{ fontWeight: "bold" }}>
                {Math.min(currentPage * itemsPerPage, totalRows)}
              </span>
              {` of `}
              <span style={{ fontWeight: "bold" }}>{totalRows}</span>
              {` results`}
            </Text>
          </div>

          <div className={classes.rightContainer}>
            <div className={classes.rowsPerPageSelector}>
              <Select
                placeholder="Rows per page"
                data={["10", "20", "50"]}
                value={itemsPerPage.toString()}
                onChange={(value) =>
                  handleRowsPerPageChange(parseInt(value as string, 10))
                }
              />
            </div>

            <div className={classes.paginationSelector}>
              <Pagination
                color="blueviolet"
                value={currentPage}
                total={Math.ceil(totalRows / itemsPerPage)}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPreviousPage={() => handlePageChange(currentPage - 1)}
                onChange={(value: number) => handleSelectPage(value)}
              />
            </div>
          </div>
        </div>

        <AddCustomer
          isVisible={isAddCustomerFormVisible}
          onClose={handleCloseModal}
          onAddCustomer={handleAddCustomer}
        />

        {isViewMode && (
          <Modal
            opened={true}
            title={
              <div style={{ textAlign: "center", fontWeight: "bold" }}>
                View Customer{" "}
              </div>
            }
            onClose={() => setViewMode(false)}
            size="md"
            padding="lg"
          >
            <div className={classes.addCustomerForm}>
              {/* Display fields for viewing, but disable editing */}
              <TextInput label="Name" value={selectedRow?.name} readOnly />
              <TextInput label="Email" value={selectedRow?.email} readOnly />
              <TextInput
                label="Contact No."
                value={selectedRow?.contactNo}
                leftSection={<Text fz={14} c='#2D2D2D' lh={'normal'}>+60</Text>}
                readOnly
              />
              <TextInput
                label="IC No."
                value={selectedRow?.nric}
                readOnly
              />
              <TextInput
                label="IC Fullname"
                value={selectedRow?.nric_fullname}
                readOnly
              />
              <TextInput
                label="DOB"
                value={formatDD_MM_YYYY(selectedRow?.dob)}
                readOnly
              />
              <TextInput
                label="Referral"
                value={selectedRow?.referral}
                readOnly
              />

              {/* <TextInput
                label="Dealer"
                value={selectedRow?.isDealer}
                readOnly
              /> */}
                {selectedRow?.isDealer ? 
                    <TextInput
                        label="Company"
                        value={selectedRow?.company}
                        readOnly
                    />
                    :
                    null
                }
              

              <TextInput label="Coins" value={selectedRow?.coins} readOnly />

              <TextInput label="Cash" value={selectedRow?.cash} readOnly />
            </div>
          </Modal>
        )}

        <EditCustomer
          isOpen={isEditMode}
          onClose={() => setEditMode(false)}
          onSaveChanges={handleSaveChanges}
          initialData={
            selectedRow ?? {
              name: "",
              email: "",
              nric: "",
              nric_fullname: "",
              contactNo: "",
              profilePicture: "",
              expoPushToken: "",
              referral: "",
              tempCode: "",
              tempCodeTime: "",
              membershipType: "",
              isDealer: "",
              company: "",
              coins: "",
              cash: "",
              daily: "",
              action: "",
              uid: "",
              dob: "",
              isVerified: null,
              isFastPass: null,
              createdAt: "",
              isCorporate: false,
              deleted: false,
            }
          }
        />

        {isDeleteConfirmationVisible && (
          <DeleteCustomer
            isOpen={true}
            onClose={handleCancelDelete}
            onConfirm={handleConfirmDelete}
          />
        )}

        {isRestoreConfirmationVisible && (
          <RestoreCustomer
            isOpen={true}
            onClose={handleCancelRestore}
            onConfirm={handleConfirmRestore}
          />
        )}

            <EnableFastPass
                isOpen={isFastPassModalVisible}
                onClose={()=>setFastPassModalVisible(false)}
                onConfirm={handleConfirmEnableFastPass}
                selectedRow={selectedRow}
            />
      </ScrollArea>
    </div>
  );
};

export default Member;
